import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Save, Building2, CreditCard, Hash, FileText, MapPin, Plus, Trash2, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BillingSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({});
  const [shippingAddrs, setShippingAddrs] = useState([]);
  const [newAddr, setNewAddr] = useState({ alias: '', direccion_completa: '', persona_contacto: '', telefono_contacto: '', is_default: false });
  const canEdit = user?.role === 'Administrador';

  useEffect(() => {
    fetchSettings();
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const r = await axios.get(`${API_URL}/billing/shipping-addresses`);
      setShippingAddrs(r.data);
    } catch { /* ignore */ }
  };

  const addAddress = async () => {
    if (!newAddr.alias || !newAddr.direccion_completa) {
      toast.error('Introduce alias y dirección');
      return;
    }
    try {
      await axios.post(`${API_URL}/billing/shipping-addresses`, newAddr);
      toast.success('Dirección añadida');
      setNewAddr({ alias: '', direccion_completa: '', persona_contacto: '', telefono_contacto: '', is_default: false });
      fetchAddresses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const setDefault = async (addr) => {
    try {
      await axios.put(`${API_URL}/billing/shipping-addresses/${addr.id}`, { ...addr, is_default: true });
      toast.success('Marcada como predeterminada');
      fetchAddresses();
    } catch (err) { toast.error('Error'); }
  };

  const deleteAddr = async (id) => {
    if (!window.confirm('¿Eliminar esta dirección de envío?')) return;
    try {
      await axios.delete(`${API_URL}/billing/shipping-addresses/${id}`);
      toast.success('Eliminada');
      fetchAddresses();
    } catch (err) { toast.error('Error'); }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/billing/company`);
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar los ajustes fiscales');
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = { ...data };
      // Convert numeric fields
      ['numero_factura_actual','numero_rectificativa_actual','numero_presupuesto_actual',
       'numero_albaran_actual','numero_pedido_actual','dias_vencimiento_default'].forEach(k=>{
        if (payload[k] !== undefined && payload[k] !== null && payload[k] !== '') payload[k] = parseInt(payload[k]);
      });
      if (payload.iva_default !== undefined && payload.iva_default !== null && payload.iva_default !== '') {
        payload.iva_default = parseFloat(payload.iva_default);
      }
      const res = await axios.put(`${API_URL}/billing/company`, payload);
      setData(res.data);
      toast.success('Ajustes guardados correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Layout><div className="text-center py-12 text-slate-500">Cargando...</div></Layout>;
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8" data-testid="billing-settings-page">
        <div>
          <BackButton to="/billing" label="Volver a Intranet" />
          <h1 className="text-3xl font-manrope font-bold text-slate-900">Ajustes de Facturación</h1>
          <p className="text-slate-500 mt-1">Datos fiscales de tu empresa, series de numeración y opciones por defecto</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Datos fiscales */}
          <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <Building2 className="w-5 h-5 text-[#004E89]" />
              <h2 className="text-lg font-manrope font-semibold text-slate-900">Datos fiscales de la empresa</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Razón social *</Label>
                <Input data-testid="settings-razon-social" value={data.razon_social || ''} onChange={e=>set('razon_social', e.target.value)} disabled={!canEdit} required />
              </div>
              <div>
                <Label>CIF/NIF *</Label>
                <Input data-testid="settings-cif" value={data.cif || ''} onChange={e=>set('cif', e.target.value)} disabled={!canEdit} required />
              </div>
              <div>
                <Label>Código país (ISO)</Label>
                <Input value={data.codigo_pais || ''} onChange={e=>set('codigo_pais', e.target.value)} disabled={!canEdit} maxLength={2} />
              </div>
              <div className="md:col-span-2">
                <Label>Dirección</Label>
                <Input value={data.direccion || ''} onChange={e=>set('direccion', e.target.value)} disabled={!canEdit} />
              </div>
              <div>
                <Label>Código postal</Label>
                <Input value={data.codigo_postal || ''} onChange={e=>set('codigo_postal', e.target.value)} disabled={!canEdit} />
              </div>
              <div>
                <Label>Población</Label>
                <Input value={data.poblacion || ''} onChange={e=>set('poblacion', e.target.value)} disabled={!canEdit} />
              </div>
              <div>
                <Label>Provincia</Label>
                <Input value={data.provincia || ''} onChange={e=>set('provincia', e.target.value)} disabled={!canEdit} />
              </div>
              <div>
                <Label>País</Label>
                <Input value={data.pais || ''} onChange={e=>set('pais', e.target.value)} disabled={!canEdit} />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={data.email || ''} onChange={e=>set('email', e.target.value)} disabled={!canEdit} />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={data.telefono || ''} onChange={e=>set('telefono', e.target.value)} disabled={!canEdit} />
              </div>
              <div className="md:col-span-2">
                <Label>Web</Label>
                <Input value={data.web || ''} onChange={e=>set('web', e.target.value)} disabled={!canEdit} placeholder="https://..." />
              </div>
            </div>
          </section>

          {/* Bancarios */}
          <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <CreditCard className="w-5 h-5 text-[#004E89]" />
              <h2 className="text-lg font-manrope font-semibold text-slate-900">Datos bancarios</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>IBAN</Label>
                <Input data-testid="settings-iban" value={data.iban || ''} onChange={e=>set('iban', e.target.value)} disabled={!canEdit} placeholder="ES00 0000 0000 0000 0000 0000" />
              </div>
              <div>
                <Label>Banco</Label>
                <Input value={data.banco || ''} onChange={e=>set('banco', e.target.value)} disabled={!canEdit} />
              </div>
            </div>
          </section>

          {/* Numeraciones */}
          <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <Hash className="w-5 h-5 text-[#004E89]" />
              <h2 className="text-lg font-manrope font-semibold text-slate-900">Series de numeración</h2>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Al emitir cada documento se asigna el próximo número y el contador se incrementa automáticamente. Puedes ajustar el contador manualmente si tu numeración se comparte con otro programa.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label>Prefijo facturas</Label>
                <Input value={data.serie_facturas_prefijo || ''} onChange={e=>set('serie_facturas_prefijo', e.target.value)} disabled={!canEdit} placeholder="" />
              </div>
              <div>
                <Label>Nº factura actual *</Label>
                <Input data-testid="settings-numero-factura" type="number" value={data.numero_factura_actual ?? 75} onChange={e=>set('numero_factura_actual', e.target.value)} disabled={!canEdit} />
              </div>
              <div className="hidden lg:block" />

              <div>
                <Label>Prefijo rectificativas</Label>
                <Input value={data.serie_rectificativas_prefijo || ''} onChange={e=>set('serie_rectificativas_prefijo', e.target.value)} disabled={!canEdit} placeholder="RECT-" />
              </div>
              <div>
                <Label>Nº rectificativa actual</Label>
                <Input type="number" value={data.numero_rectificativa_actual ?? 1} onChange={e=>set('numero_rectificativa_actual', e.target.value)} disabled={!canEdit} />
              </div>
              <div className="hidden lg:block" />

              <div>
                <Label>Prefijo presupuestos</Label>
                <Input value={data.serie_presupuestos_prefijo || ''} onChange={e=>set('serie_presupuestos_prefijo', e.target.value)} disabled={!canEdit} placeholder="PRES-" />
              </div>
              <div>
                <Label>Nº presupuesto actual</Label>
                <Input type="number" value={data.numero_presupuesto_actual ?? 1} onChange={e=>set('numero_presupuesto_actual', e.target.value)} disabled={!canEdit} />
              </div>
              <div className="hidden lg:block" />

              <div>
                <Label>Prefijo albaranes</Label>
                <Input value={data.serie_albaranes_prefijo || ''} onChange={e=>set('serie_albaranes_prefijo', e.target.value)} disabled={!canEdit} placeholder="ALB-" />
              </div>
              <div>
                <Label>Nº albarán actual</Label>
                <Input type="number" value={data.numero_albaran_actual ?? 1} onChange={e=>set('numero_albaran_actual', e.target.value)} disabled={!canEdit} />
              </div>
              <div className="hidden lg:block" />

              <div>
                <Label>Prefijo pedidos</Label>
                <Input value={data.serie_pedidos_prefijo || ''} onChange={e=>set('serie_pedidos_prefijo', e.target.value)} disabled={!canEdit} placeholder="PED-" />
              </div>
              <div>
                <Label>Nº pedido actual</Label>
                <Input type="number" value={data.numero_pedido_actual ?? 1} onChange={e=>set('numero_pedido_actual', e.target.value)} disabled={!canEdit} />
              </div>
            </div>
          </section>

          {/* Defaults */}
          <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-[#004E89]" />
              <h2 className="text-lg font-manrope font-semibold text-slate-900">Valores por defecto</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>IVA por defecto (%)</Label>
                <Input type="number" step="0.01" value={data.iva_default ?? 21} onChange={e=>set('iva_default', e.target.value)} disabled={!canEdit} />
                <p className="text-xs text-slate-500 mt-1">Valores España: 0, 4, 10, 21</p>
              </div>
              <div>
                <Label>Días de vencimiento</Label>
                <Input type="number" value={data.dias_vencimiento_default ?? 30} onChange={e=>set('dias_vencimiento_default', e.target.value)} disabled={!canEdit} />
              </div>
              <div>
                <Label>Forma de pago</Label>
                <select
                  className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={data.forma_pago_default || 'Transferencia'}
                  onChange={e=>set('forma_pago_default', e.target.value)}
                  disabled={!canEdit}
                >
                  <option>Transferencia</option>
                  <option>Domiciliación</option>
                  <option>Efectivo</option>
                  <option>Tarjeta</option>
                  <option>Otro</option>
                </select>
              </div>
            </div>
          </section>

          {canEdit && (
            <div className="flex justify-end">
              <Button type="submit" disabled={saving} data-testid="settings-save-button" className="bg-[#004E89] hover:bg-[#003a68]">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </div>
          )}
        </form>

        {/* Direcciones de envío predefinidas */}
        <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="shipping-addresses-section">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-[#004E89]" />
            <h2 className="text-lg font-manrope font-semibold text-slate-900">Direcciones de envío predefinidas</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            Guarda aquí las direcciones de entrega que uses habitualmente en los pedidos a proveedores.
            La marcada como <b>predeterminada</b> se carga automáticamente en cada nuevo pedido.
          </p>

          {/* Lista de direcciones existentes */}
          {shippingAddrs.length > 0 && (
            <div className="space-y-2 mb-6">
              {shippingAddrs.map(a => (
                <div key={a.id} className="border border-slate-200 rounded-lg p-3 flex items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{a.alias}</span>
                      {a.is_default && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                          <Star className="w-3 h-3" /> Predeterminada
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 whitespace-pre-wrap mt-1">{a.direccion_completa}</div>
                    {(a.persona_contacto || a.telefono_contacto) && (
                      <div className="text-xs text-slate-500 mt-1">
                        {a.persona_contacto && `Contacto: ${a.persona_contacto}`}
                        {a.persona_contacto && a.telefono_contacto && ' · '}
                        {a.telefono_contacto && `Tel: ${a.telefono_contacto}`}
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex gap-1">
                      {!a.is_default && (
                        <button onClick={()=>setDefault(a)} title="Marcar como predeterminada" className="p-1 text-slate-500 hover:text-amber-600">
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={()=>deleteAddr(a.id)} title="Eliminar" data-testid={`del-addr-${a.id}`} className="p-1 text-slate-500 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Añadir nueva */}
          {canEdit && (
            <div className="border-t border-slate-200 pt-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Añadir nueva dirección</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Alias *</Label>
                  <Input value={newAddr.alias} onChange={e=>setNewAddr({...newAddr, alias: e.target.value})} placeholder="p.ej. Sede central IRTA" data-testid="new-addr-alias" />
                </div>
                <div>
                  <Label>Persona de contacto</Label>
                  <Input value={newAddr.persona_contacto} onChange={e=>setNewAddr({...newAddr, persona_contacto: e.target.value})} />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input value={newAddr.telefono_contacto} onChange={e=>setNewAddr({...newAddr, telefono_contacto: e.target.value})} />
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <input type="checkbox" id="new-addr-default" checked={newAddr.is_default} onChange={e=>setNewAddr({...newAddr, is_default: e.target.checked})} />
                  <label htmlFor="new-addr-default" className="text-sm">Marcar como predeterminada</label>
                </div>
                <div className="md:col-span-2">
                  <Label>Dirección completa *</Label>
                  <textarea value={newAddr.direccion_completa} onChange={e=>setNewAddr({...newAddr, direccion_completa: e.target.value})} rows={3} placeholder="Paseo Héroes de Marruecos, 65&#10;12570 Alcalà de Xivert (Castellón)" className="w-full mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="new-addr-completa" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button type="button" onClick={addAddress} data-testid="add-addr-button" className="bg-[#004E89] hover:bg-[#003a68]">
                  <Plus className="w-4 h-4 mr-1" /> Añadir dirección
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

export default BillingSettings;
